import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, User, ArrowRight, Phone, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Blog = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const posts = [
    {
      title: 'How Cool Roof Coatings Slash Florida AC Bills by 30%',
      excerpt: 'Discover the science behind reflective roof coatings and why they\'re essential for Central Florida homeowners facing brutal summer heat.',
      author: 'Jason - Savage Rejuvenation',
      date: 'March 10, 2026',
      dateISO: '2026-03-10',
      slug: 'cool-roof-coatings-reduce-ac-bills-florida',
      image: 'https://images.unsplash.com/photo-1674485169641-bcb2bf6f1df9?crop=entropy&cs=srgb&fm=jpg&q=85',
      tags: ['Energy Savings', 'Cool Roofs', 'Florida']
    },
    {
      title: 'Hurricane Season Prep: Protect Your Roof Before the Storm',
      excerpt: 'Essential tips for hurricane-proofing your Central Florida roof. Learn how professional coatings provide waterproof, wind-resistant protection.',
      author: 'Lainie - Savage Rejuvenation',
      date: 'March 5, 2026',
      dateISO: '2026-03-05',
      slug: 'hurricane-season-roof-protection-florida',
      image: 'https://images.unsplash.com/photo-1653889676142-9832054b9911?crop=entropy&cs=srgb&fm=jpg&q=85',
      tags: ['Hurricane Prep', 'Roof Protection', 'Florida Weather']
    },
    {
      title: 'Roof Coating vs. Roof Replacement: Which Saves You More?',
      excerpt: 'A detailed cost comparison showing why roof coating is the smarter investment for most Central Florida homeowners.',
      author: 'Jason - Savage Rejuvenation',
      date: 'February 28, 2026',
      dateISO: '2026-02-28',
      slug: 'roof-coating-vs-replacement-cost-comparison',
      image: 'https://images.unsplash.com/photo-1645892399945-bad477acd3e3?crop=entropy&cs=srgb&fm=jpg&q=85',
      tags: ['Cost Savings', 'Roof Maintenance', 'Investment']
    },
    {
      title: 'The Ultimate Guide to Metal Roof Coatings in Orlando & Tampa',
      excerpt: 'Everything you need to know about coating metal roofs in Florida\'s harsh climate. Prevent rust, reduce heat, extend roof life.',
      author: 'Lainie - Savage Rejuvenation',
      date: 'February 20, 2026',
      dateISO: '2026-02-20',
      slug: 'metal-roof-coating-guide-orlando-tampa',
      image: 'https://images.unsplash.com/photo-1680035579849-3185807fb1ea?crop=entropy&cs=srgb&fm=jpg&q=85',
      tags: ['Metal Roofs', 'Orlando', 'Tampa', 'Roof Coating']
    },
    {
      title: '5 Signs Your Florida Roof Needs Coating (Not Replacement)',
      excerpt: 'Learn to identify when your roof is a perfect candidate for coating. Save thousands while extending your roof\'s life by 10+ years.',
      author: 'Jason - Savage Rejuvenation',
      date: 'February 15, 2026',
      dateISO: '2026-02-15',
      slug: 'signs-florida-roof-needs-coating',
      image: 'https://images.unsplash.com/photo-1681049400158-0ff6249ac315?crop=entropy&cs=srgb&fm=jpg&q=85',
      tags: ['Roof Maintenance', 'Warning Signs', 'Prevention']
    },
    {
      title: 'Why UV-Resistant Coatings Are Essential in Central Florida',
      excerpt: 'Florida\'s intense UV rays destroy roofs faster than anywhere else. Here\'s how nano-ceramic coatings protect your investment.',
      author: 'Lainie - Savage Rejuvenation',
      date: 'February 10, 2026',
      dateISO: '2026-02-10',
      slug: 'uv-resistant-roof-coatings-florida',
      image: 'https://images.unsplash.com/photo-1674485169641-bcb2bf6f1df9?crop=entropy&cs=srgb&fm=jpg&q=85',
      tags: ['UV Protection', 'Roof Longevity', 'Florida Climate']
    }
  ];

  // Blog/Article Schema
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Savage Rejuvenation Coatings Blog",
    "description": "Expert advice on roof coating, energy savings, hurricane protection, and roof maintenance for Central Florida homeowners.",
    "url": "https://savagerejuvenationcoatings.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Savage Rejuvenation Coatings",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png"
      }
    },
    "blogPost": posts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image,
      "datePublished": post.dateISO,
      "author": {
        "@type": "Person",
        "name": post.author.split(' - ')[0]
      },
      "publisher": {
        "@type": "Organization",
        "name": "Savage Rejuvenation Coatings"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://savagerejuvenationcoatings.com/blog/${post.slug}`
      },
      "keywords": post.tags.join(', ')
    }))
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://savagerejuvenationcoatings.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://savagerejuvenationcoatings.com/blog"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Roof Coating Blog | Expert Tips & Guides Orlando Tampa FL | Savage Rejuvenation Coatings</title>
        <meta name="description" content="Expert advice on roof coating, energy savings, hurricane protection, and roof maintenance for Central Florida homeowners. Tips from licensed contractors serving Orlando, Tampa, Lakeland, Kissimmee & beyond." />
        <meta name="keywords" content="roof coating blog, Florida roofing tips, hurricane roof protection guide, cool roof benefits, Orlando roofing advice, Tampa roof maintenance, energy efficient roofing Florida" />
        
        <meta property="og:title" content="Roof Coating Blog | Expert Tips | Savage Rejuvenation Coatings" />
        <meta property="og:description" content="Expert roof coating advice for Central Florida homeowners. Energy savings, hurricane prep, and maintenance tips." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://savagerejuvenationcoatings.com/blog" />
        
        <link rel="canonical" href="https://savagerejuvenationcoatings.com/blog" />
        <meta name="robots" content="index, follow" />
        
        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-secondary border-b-4 border-primary">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3">
            <div className="flex items-center justify-between">
              <nav className="hidden lg:flex items-center gap-6">
                <a href="/" className="text-white hover:text-primary font-manrope font-semibold">Home</a>
                <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold">Reviews</a>
                <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold">Q&A</a>
                <a href="/blog" className="text-primary font-manrope font-semibold">Blog</a>
              </nav>
              <div className="hidden lg:flex items-center gap-6">
                <a href="tel:3218044413" className="text-white hover:text-primary transition-colors flex items-center gap-2 text-lg font-manrope font-semibold">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </div>
              <div className="lg:hidden flex items-center gap-3">
                <a href="tel:3218044413">
                  <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[2px_2px_0px_0px_#000] rounded-sm px-3 py-2">
                    <Phone className="h-5 w-5" />
                  </Button>
                </a>
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-white p-2"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <nav className="lg:hidden mt-4 pb-4 border-t border-gray-600 pt-4 flex flex-col gap-4">
                <a href="/" className="text-white hover:text-primary font-manrope font-semibold text-lg">Home</a>
                <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold text-lg">Reviews</a>
                <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold text-lg">Q&A</a>
                <a href="/blog" className="text-primary font-manrope font-semibold text-lg">Blog</a>
                <a href="tel:3218044413" className="text-primary font-manrope font-semibold text-lg flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </nav>
            )}
          </div>
        </header>

        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h1 className="font-barlow text-5xl font-bold uppercase mb-4">Roof Coating Blog</h1>
              <p className="text-xl font-manrope text-gray-700">Expert advice for Central Florida homeowners</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, idx) => (
                <article key={idx} className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000] overflow-hidden hover:-translate-y-1 transition-transform">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.tags.map((tag, tagIdx) => (
                        <span key={tagIdx} className="text-xs px-2 py-1 bg-primary/10 text-primary font-semibold border border-primary">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="font-barlow text-xl font-bold uppercase mb-3 leading-tight">{post.title}</h2>
                    <p className="font-manrope text-gray-700 text-sm mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{post.date}</span>
                      </div>
                    </div>
                    <a 
                      href={`#${post.slug}`}
                      className="inline-flex items-center gap-2 text-primary font-manrope font-bold hover:underline"
                    >
                      Read More <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-16 bg-primary border-2 border-black shadow-[6px_6px_0px_0px_#000] p-8 text-white text-center">
              <h2 className="font-barlow text-3xl font-bold uppercase mb-4">Get Expert Roof Advice</h2>
              <p className="text-lg font-manrope mb-6">Have specific questions about your roof? Call us for a FREE consultation!</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:3218044413" className="bg-white hover:bg-gray-100 text-black px-8 py-4 border-2 border-black shadow-[4px_4px_0px_0px_#000] font-barlow uppercase font-bold inline-block">
                  Call: (321) 804-4413
                </a>
              </div>
            </div>

            <div className="mt-12 bg-accent-light border-2 border-black shadow-[4px_4px_0px_0px_#000] p-6">
              <h3 className="font-barlow text-2xl font-bold uppercase mb-4">Popular Topics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Energy Savings', 'Hurricane Protection', 'Cost Comparison', 'Roof Maintenance', 'Metal Roofs', 'Orlando Tips', 'Tampa Guides', 'DIY Prevention'].map((topic, idx) => (
                  <a 
                    key={idx}
                    href={`#${topic.toLowerCase().replace(/\s+/g, '-')}`}
                    className="bg-white border-2 border-black shadow-[2px_2px_0px_0px_#000] p-3 text-center hover:-translate-y-1 transition-transform"
                  >
                    <span className="font-manrope font-semibold text-sm">{topic}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
